/**
 * Calendar dates in a tenant's own timezone.
 *
 * A due date is a day, not an instant. "Due 1 October" means the whole of 1 October in the shop's
 * city — so comparing it against a UTC instant marks a Cairo instalment overdue at 10pm on the
 * 30th, and a Dubai one two hours earlier still. Every date decision in receivables goes through
 * here with the tenant's IANA zone.
 */

/** `YYYY-MM-DD`. */
export type BusinessDate = string;

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Whether the runtime recognises this IANA identifier.
 *
 * Asks the platform rather than checking against a hard-coded list: the tz database gains and
 * merges zones, and a list baked in here would reject a legitimate zone a year from now.
 */
export function isSupportedTimezone(value: string): boolean {
  if (typeof value !== 'string' || value.trim() === '') return false;

  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** Zones offered in the UI. Not a validation list — `isSupportedTimezone` accepts any real zone. */
export const COMMON_TIMEZONES: readonly string[] = [
  'Africa/Cairo',
  'Asia/Riyadh',
  'Asia/Dubai',
  'Asia/Kuwait',
  'Asia/Qatar',
  'Asia/Bahrain',
  'Asia/Muscat',
  'Asia/Amman',
  'Asia/Beirut',
  'Africa/Casablanca',
  'Africa/Tunis',
  'Africa/Algiers',
  'Africa/Tripoli',
  'Africa/Khartoum',
  'Asia/Baghdad',
  'Europe/London',
  'UTC',
];

/**
 * The calendar date it is right now in a given zone.
 *
 * `en-CA` because its short date format is ISO-shaped, which avoids reassembling parts by hand.
 */
export function businessDateIn(timezone: string, at: Date = new Date()): BusinessDate {
  const zone = isSupportedTimezone(timezone) ? timezone : 'UTC';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/** Reads a stored `Date` as the calendar day it represents, without shifting it through a zone. */
export function toBusinessDate(value: Date | string): BusinessDate {
  if (typeof value === 'string') {
    if (DATE_PATTERN.test(value)) return value;
    return value.slice(0, 10);
  }
  return value.toISOString().slice(0, 10);
}

/**
 * Turns a business date back into a `Date` for storage.
 *
 * Midnight UTC, deliberately: the column is `@db.Date`, which carries no time, and anchoring at
 * UTC keeps the round trip through Prisma lossless in every deployment zone.
 */
export function fromBusinessDate(date: BusinessDate): Date {
  return new Date(`${date}T00:00:00.000Z`);
}

export function isBusinessDate(value: string): boolean {
  return DATE_PATTERN.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00.000Z`));
}

/**
 * Strictly `YYYY-MM-DD`, naming a day that exists.
 *
 * Stricter than `isBusinessDate`, which lets the runtime roll 2026-02-30 forward into March: here the date must survive a round trip unchanged, so 2028-02-29 passes and 2026-02-29 does not. A timestamp, with or without an offset, is never a calendar date.
 */
export function isCalendarDate(value: unknown): value is BusinessDate {
  if (typeof value !== 'string' || !DATE_PATTERN.test(value)) return false;
  const anchored = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(anchored.getTime()) && anchored.toISOString().slice(0, 10) === value;
}

/** Whole days from `from` to `to`. Negative when `to` is earlier. */
export function daysBetween(from: BusinessDate, to: BusinessDate): number {
  const ms = fromBusinessDate(to).getTime() - fromBusinessDate(from).getTime();
  return Math.round(ms / 86_400_000);
}

export function addDays(date: BusinessDate, days: number): BusinessDate {
  const shifted = new Date(fromBusinessDate(date).getTime() + days * 86_400_000);
  return shifted.toISOString().slice(0, 10);
}

/**
 * Adds calendar months, clamping to the end of the target month.
 *
 * 31 January plus one month is 28 February, not 3 March. A schedule that silently walks forward
 * every month puts the fifth instalment on a date nobody agreed to.
 */
export function addMonths(date: BusinessDate, months: number): BusinessDate {
  const base = fromBusinessDate(date);
  const year = base.getUTCFullYear();
  const month = base.getUTCMonth();
  const day = base.getUTCDate();

  const target = new Date(Date.UTC(year, month + months, 1));
  const daysInTarget = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();

  target.setUTCDate(Math.min(day, daysInTarget));
  return target.toISOString().slice(0, 10);
}
